<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { formatPool, defaultHandle } from "@/utils";

import NavigationWrapper from "@/components/NavigationWrapper";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InfoTooltip from "@/components/InfoTooltip";

export default {
  // Included components
  components: {
    NavigationWrapper,
    FontAwesomeIcon,
    InfoTooltip,
  },

  data() {
    return {
      // The ID of the worker being edited, or null if creating
      workerId: this.workerId,

      // The names of the workers
      namesListRaw: "",

      // Format string for the worker name
      nameFormat: "",

      // The handles for the workers
      handlesListRaw: "",

      // Format string for the worker handle
      handleFormat: "",

      // The serial numbers for worker units
      serialNumbersListRaw: "",

      // The ID of the device model
      deviceId: null,

      // The destination worker name format
      poolWorkerNameFormat: "",

      // The list of pool worker names
      poolWorkerNamesListRaw: "",

      // The destination worker password
      poolWorkerPassword: "",

      // The ID of the pool entry to connect to
      poolId: null,

      // How the user wants to add the workers
      namingScheme: "numeric",

      // Count of new workers to add
      count: null,

      // Starting index to start counting off workers
      startIndex: 1,

      // Whether the summary section is open
      summaryOpen: false,

      // The group ID to add the workers to, or null
      groupId: null,
    };
  },

  computed: {
    // Options for the device selector
    deviceOptions() {
      return this.deviceTypes.map((type) => {
        return { value: type.id, text: type.name };
      });
    },

    // Options for the pool selector
    poolOptions() {
      return this.pools.map((pool) => {
        return {
          value: pool.id,
          text: formatPool(pool, true),
        };
      });
    },

    // Options for the group selector
    groupOptions() {
      return Object.values(this.groups).map((group) => {
        return {
          value: group.id,
          text: group.name,
        };
      });
    },

    // Max number of workers the user can add
    maxToAdd() {
      if (this.license.max_workers == null) {
        return 150000;
      } else {
        return this.license.max_workers - this.teamStatistics.count;
      }
    },

    // The split and trimmed list of names
    namesList() {
      if (this.namingScheme == "numeric") {
        return Array.from({ length: this.count }, (x, i) =>
          this.doBasicFormat(this.nameFormat, i + parseInt(this.startIndex))
        );
      } else {
        return this.namesListRaw
          .split(/,|\n/)
          .map((name) => name.trim())
          .filter((name) => name);
      }
    },

    // The split and trimmed list of handles
    handlesList() {
      if (this.namingScheme == "numeric") {
        return Array.from({ length: this.count }, (x, i) =>
          this.doBasicFormat(this.handleFormat, i + parseInt(this.startIndex))
        );
      } else {
        return this.handlesListRaw
          .split(/,|\n/)
          .map((handle) => handle.trim())
          .filter((handle) => handle);
      }
    },

    // The split and trimmed list of serial numbers
    serialNumbersList() {
      return this.serialNumbersListRaw
        .split(/,|\n/)
        .map((serial_number) => serial_number.trim())
        .filter((serial_number) => serial_number);
    },

    // The split and trimmed list of destination worker names
    poolWorkerNamesList() {
      if (this.namingScheme == "numeric") {
        return Array.from({ length: this.count }, (x, i) =>
          this.doBasicFormat(
            this.poolWorkerNameFormat,
            i + parseInt(this.startIndex)
          )
        );
      } else {
        return this.poolWorkerNamesListRaw
          .split(/,|\n/)
          .map((worker_name) => worker_name.trim())
          .filter((worker_name) => worker_name);
      }
    },

    // Whether there are any duplicate handles
    anyDuplicateHandles() {
      return new Set(this.handlesList).size !== this.handlesList.length;
    },

    // Data for the workers to post to the API
    workerData() {
      let count = this.namesList.length;
      return Array.from({ length: count }, (x, i) => {
        // Handling omitted serial numbers
        var serial_number_holder = null;
        if (i < this.serialNumbersList.length) {
          serial_number_holder = this.serialNumbersList[i];
        }

        return {
          name: this.namesList[i],
          handle: this.handlesList[i],
          serial_number: serial_number_holder,
          device_id: this.deviceId,
          password: "",
          notes: "",
          pool_configuration: {
            worker_name: this.poolWorkerNamesList[i],
            password: this.poolWorkerPassword,
            pool_id: this.poolId,
          },
        };
      });
    },

    // Validate form data and return an error code or null
    error() {
      if (this.namesList.length > this.maxToAdd) {
        return "HIGH_COUNT";
      }

      let numeric = this.namingScheme == "numeric";
      let list = !numeric;

      if (numeric && this.count == null) {
        return "MISSING_COUNT";
      } else if (numeric && this.nameFormat.trim() == "") {
        return "MISSING_NAME_FORMAT";
      } else if (numeric && this.handleFormat.trim() == "") {
        return "MISSING_HANDLE_FORMAT";
      } else if (numeric && this.namesList[0] == null) {
        return "INVALID_NAME_FORMAT";
      } else if (numeric && this.handlesList[0] == null) {
        return "INVALID_HANDLE_FORMAT";
      }

      if (this.anyDuplicateHandles) {
        return "DUPLICATE_HANDLE";
      }

      if (this.deviceId == null) {
        return "MISSING_DEVICE";
      } else if (this.poolId == null) {
        return "MISSING_POOL";
      }

      if (numeric && this.poolWorkerNameFormat.trim() == "") {
        return "MISSING_POOL_WORKER_NAME_FORMAT";
      } else if (numeric && this.poolWorkerNamesList[0] == null) {
        return "INVALID_POOL_WORKER_NAME_FORMAT";
      }

      if (list && this.namesList.length == 0) {
        return "MISSING_WORKER_NAMES";
      } else if (list && this.namesList.length !== this.handlesList.length) {
        return "MISMATCHED_HANDLES_COUNT";
      } else if (
        list &&
        this.namesList.length !== this.poolWorkerNamesList.length
      ) {
        return "MISMATCHED_POOL_WORKER_NAMES_COUNT";
      }

      return null;
    },

    // Mapped vuex state
    ...mapState([
      "teamStatistics",
      "license",
      "deviceTypes",
      "pools",
      "groups",
    ]),
  },

  watch: {
    // When the name changes, update the handle to be the default
    // handle, unless it has been manually changed
    // When the name changes, update the handle to be the default
    // handle, unless it has been manually changed
    namesList(value, old) {
      if (this.namingScheme !== "list") return;
      let defaultHandlesOld = old.map((name) => defaultHandle(name));
      let defaultHandlesNew = value.map((name) => defaultHandle(name));
      if (
        this.handlesListRaw == "" ||
        this.arraysEqual(this.handlesList, defaultHandlesOld)
      ) {
        this.handlesListRaw = defaultHandlesNew.join(", ");
      }
    },

    // When the name changes, update the handle to be the default
    // handle, unless it has been manually changed
    nameFormat(value, old) {
      if (value.length > 0 && value[0] == null) return;
      if (
        this.handleFormat == "" ||
        this.handleFormat == defaultHandle(old, true)
      ) {
        this.handleFormat = defaultHandle(value, true);
      }
    },
  },

  // When the component is created, fetch the worker (if in edit mode)
  // and get the pool and device options
  async created() {
    // Get device types and pools
    await Promise.all([
      this.getTeamStatistics({}),
      this.getLicense(),
      this.getDeviceTypes(),
      this.getPools(),
      this.getGroups(),
    ]);
  },

  methods: {
    // Post the new workers via the API
    async submit() {
      // Check if there are no errors before submitting

      if (this.error == null) {
        // Post either to the team or the group, depending
        // on whether the user opted to add to a group
        let post =
          this.groupId == null ? this.postWorkers : this.postGroupWorkers;

        // Update given the form inputs
        const success = await post({
          groupId: this.groupId,
          workers: this.workerData,
        });

        // If success, then redirect
        if (success) {
          await this.$router.push("/workers");
        }
      } else {
        this.handleErrors();
      }
    },

    // Function that handles errors
    handleErrors() {
      // Use a shorter name for this.$createElement
      const h = this.$createElement;

      switch (this.error) {
        case "HIGH_COUNT":
          this.pushToast({
            title: "High Count Error",
            message: h("div", [
              `Your license only supports adding ${this.maxToAdd} more worker${
                this.maxToAdd == 1 ? "" : "s"
              }.
              Reduce the number of workers to at most ${this.maxToAdd}.`,
            ]),
            variant: "danger",
          });
          break;

        case "DUPLICATE_HANDLE":
          var errorMessage1 = "Handles must be unique.";
          var errorMessage2 = "";

          if (this.namingScheme == "numeric") {
            var number = h("b", [" %d "]);
            errorMessage2 = h("span", [
              "  Include",
              number,
              "in the handle to include the worker's number.",
            ]);
          }

          var errorMessage3 = h("span", [
            "  Please ensure that each handle appears only once.",
          ]);
          var errorMessageHTML = h("div", [
            errorMessage1,
            errorMessage2,
            errorMessage3,
          ]);

          this.pushToast({
            title: "Duplicate Handle Error",
            message: errorMessageHTML,
            variant: "danger",
          });
          break;

        case "MISSING_COUNT":
          this.pushToast({
            title: "Missing Count Error",
            message: `Count is required.`,
            variant: "danger",
          });
          break;

        case "MISSING_NAME_FORMAT":
          this.pushToast({
            title: "Missing Name Format Error",
            message: `Name format is required.`,
            variant: "danger",
          });
          break;

        case "MISSING_HANDLE_FORMAT":
          this.pushToast({
            title: "Missing Handle Format Error",
            message: `Handle format is required.`,
            variant: "danger",
          });
          break;

        case "INVALID_NAME_FORMAT":
          this.pushToast({
            title: "Invalid Name Format Error",
            message: h("div", [`Invalid name format.`]),
            variant: "danger",
          });
          break;

        case "INVALID_HANDLE_FORMAT":
          this.pushToast({
            title: "Invalid Handle Format Error",
            message: `Invalid handle format.`,
            variant: "danger",
          });
          break;

        case "MISSING_DEVICE":
          this.pushToast({
            title: "Missing Device Error",
            message: `Please select a device type.`,
            variant: "danger",
          });
          break;

        case "MISSING_POOL":
          this.pushToast({
            title: "Missing Pool Error",
            message: `Please select a pool.`,
            variant: "danger",
          });
          break;

        case "MISSING_POOL_WORKER_NAME_FORMAT":
          this.pushToast({
            title: "Missing Pool Worker Name Format Error",
            message: `Pool worker name format is required.`,
            variant: "danger",
          });
          break;

        case "INVALID_POOL_WORKER_NAME_FORMAT":
          this.pushToast({
            title: "Invalid Pool Worker Name Format Error",
            message: `Invalid pool worker name format.`,
            variant: "danger",
          });
          break;

        case "MISSING_WORKER_NAMES":
          this.pushToast({
            title: "Missing Worker Names Error",
            message: `Enter at least one worker name.`,
            variant: "danger",
          });
          break;

        case "MISMATCHED_HANDLES_COUNT":
          this.pushToast({
            title: "Mismatched Handles Count Error",
            message: `Number of handles does not match number of worker names.`,
            variant: "danger",
          });
          break;

        case "MISMATCHED_POOL_WORKER_NAMES_COUNT":
          this.pushToast({
            title: "Mismatched Pool Worker Names Count Error",
            message: `Number of pool worker names does not match number of worker names.`,
            variant: "danger",
          });
          break;
      }
    },

    // Check if two arrays are (shallowly) equal
    arraysEqual(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (a.length != b.length) return false;

      for (var i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
      }

      return true;
    },

    // Not overly-generalized version of python's string formatting;
    // works only with one value to fill, and only with integers. Supports
    // three formats -- %% for escaped percent, %d for integer fill, and %0nd
    // for n-wise 0 padding.
    doBasicFormat(format, value) {
      // Build the resulting formatted string
      var processed = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Find the next format sequence to replace. Return if none is found,
        // otherwise, move preceding text to processed string.
        let index = format.indexOf("%");
        if (index == -1) {
          return processed + format;
        }
        processed += format.slice(0, index);

        // Remove the percent from the format string
        format = format.slice(index + 1);

        // Check for escaped percent
        if (format.startsWith("%")) {
          processed += "%";
          format = format.slice(1);
        }
        // Check for integer fill
        else if (format.startsWith("d")) {
          processed += value.toString();
          format = format.slice(1);
        }
        // Check for more complex format sequences
        else {
          // Check for zero fill integer
          let zeroFillMatch = format.match(/^0([1-9][0-9]*)d/);
          if (zeroFillMatch != null) {
            let fill = parseInt(zeroFillMatch[1]);
            if (fill > 10) return null;
            processed += value.toString().padStart(fill, "0");
            format = format.slice(zeroFillMatch[0].length);
            continue;
          }
          return null;
        }
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "getWorker",
      "postWorkers",
      "getDeviceTypes",
      "getPools",
      "getTeamStatistics",
      "getLicense",
      "getGroups",
      "postGroupWorkers",
    ]),
    ...mapMutations(["pushToast"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/workers">Workers</b-breadcrumb-item>
      <b-breadcrumb-item active>Bulk Add Workers</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">Bulk Add Workers</h1>
    <b-row>
      <b-col>
        <b-card class="mb-3">
          <b-form @submit.stop.prevent="submit">
            <b-row>
              <b-col>
                <b-form-group>
                  <template #label>
                    Naming Scheme
                    <InfoTooltip
                      text="Choose 'List of names' if you have a prepared list of names you want to assign to workers, or choose 'Numeric names' if you want names generated sequentially, e.g. 'Worker 001'."
                    />
                  </template>
                  <b-form-radio-group
                    class="boxed-radios"
                    v-model="namingScheme"
                    :options="[
                      { text: 'Numeric names', value: 'numeric' },
                      { text: 'List of names', value: 'list' },
                    ]"
                    plain
                    name="plain-inline"
                  ></b-form-radio-group>
                  <small class="form-text text-muted">
                    How you want to configure the workers' names.
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
            <div v-if="namingScheme == 'numeric'">
              <b-row>
                <b-col>
                  <b-form-group
                    id="count-group"
                    label="Count"
                    label-for="count"
                  >
                    <b-form-input
                      required
                      id="count"
                      type="number"
                      v-model="count"
                      placeholder="Count"
                      min="1"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Number of workers to add.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="start-index-group"
                    label="Starting Index"
                    label-for="start-index"
                  >
                    <b-form-input
                      required
                      id="start-index"
                      type="number"
                      v-model="startIndex"
                      min="0"
                      placeholder="Starting Index"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      At what number to start numeric worker names.
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form-group id="name-group" label-for="name">
                    <template #label>
                      Name Format
                      <InfoTooltip
                        text="A format for worker names, including '%d' to fill in the worker's number. Use '%0nd' to pad the number with up to n leading zeros, (e.g. '%02d' to produce '01')."
                      />
                    </template>
                    <b-form-input
                      required
                      id="name"
                      v-model="nameFormat"
                      placeholder="Worker %02d"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Format for human-readable names for the workers, e.g.
                      <b>Worker %02d</b>
                      &rarr;
                      <b>Worker 01</b>
                      .
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="handle-group"
                    label="Handle Format"
                    label-for="handle"
                  >
                    <input
                      id="handle"
                      v-model="handleFormat"
                      placeholder="worker-%02d"
                      autocomplete="off"
                      class="form-control"
                    />
                    <small class="form-text text-muted">
                      Format for the workers' handles, e.g.
                      <b>worker-%02d</b>
                      &rarr;
                      <b>worker-01</b>
                      . Each worker's handle is used in miner pool settings,
                      like
                      <b>my-team.worker-01</b>
                      .
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
            <div v-else>
              <b-row>
                <b-col>
                  <b-form-group id="name-group" label="Names" label-for="name">
                    <b-form-textarea
                      required
                      id="name"
                      v-model="namesListRaw"
                      placeholder="Name 1, Name 2, ..."
                      rows="4"
                      no-resize
                    ></b-form-textarea>
                    <small class="form-text text-muted">
                      Human-readable names for the workers, separated by commas
                      or newlines.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="handle-group"
                    label="Handles"
                    label-for="handle"
                  >
                    <b-form-textarea
                      id="handle"
                      v-model="handlesListRaw"
                      placeholder="name-1, name-2, ..."
                      autocomplete="off"
                      class="form-control"
                      rows="4"
                      no-resize
                    />
                    <small class="form-text text-muted">
                      Each worker's handle is used in miner pool settings, like
                      <b>my-team.the-handle</b>
                      .
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
            <b-form-group
              id="serial-number-group"
              label="Serial Numbers (Optional)"
              label-for="serial_number"
            >
              <b-form-textarea
                id="serial_number"
                v-model="serialNumbersListRaw"
                placeholder="Serial Number for Worker 1, Serial Number for Worker 2, ..."
                class="form-control"
                rows="4"
                no-resize
              ></b-form-textarea>
              <small class="form-text text-muted">
                Worker serial numbers are unique identifier for worker units.
                Numbers are separated by commas or newlines.
              </small>
            </b-form-group>
            <b-form-group
              id="device-type-group"
              label="Device Type"
              label-for="deviceId"
            >
              <b-form-select
                required
                v-model="deviceId"
                id="deviceId"
                :options="deviceOptions"
              >
                <template #first>
                  <option :value="null" disabled>Device</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted">
                Type of device connecting to this proxy.
              </small>
            </b-form-group>
            <b-form-group
              id="destination-worker-pool-group"
              label="Pool"
              label-for="destinationWorkerPool"
            >
              <b-form-select
                required
                v-model="poolId"
                id="destinationWorkerPool"
                :options="poolOptions"
              >
                <template #first>
                  <option :value="null" disabled>Pool</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted">
                Pool that the worker will connect to.
              </small>
            </b-form-group>
            <b-row>
              <b-col>
                <div v-if="namingScheme == 'numeric'">
                  <b-form-group
                    id="destination-worker-name-group"
                    label="Pool Worker Name Format"
                    label-for="destinationWorkerName"
                  >
                    <b-form-input
                      required
                      id="destinationWorkerName"
                      v-model="poolWorkerNameFormat"
                      placeholder="username.worker-%02d"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Format for names that will appear to the pool, including
                      the pool username, e.g.
                      <b>username.worker-%02d</b>
                      &rarr;
                      <b>username.worker-01</b>
                      .
                    </small>
                  </b-form-group>
                </div>
                <div v-else>
                  <b-form-group
                    id="destination-worker-name-group"
                    label="Pool Worker Names"
                    label-for="destinationWorkerName"
                  >
                    <b-form-textarea
                      required
                      id="destinationWorkerName"
                      v-model="poolWorkerNamesListRaw"
                      placeholder="Pool Worker Names"
                      rows="4"
                      autocomplete="off"
                      no-resize
                    ></b-form-textarea>
                    <small class="form-text text-muted">
                      Names that will appear to the pool, including the pool
                      username, separated by commas or newlines.
                    </small>
                  </b-form-group>
                </div>
              </b-col>
              <b-col>
                <b-form-group
                  id="destination-worker-password-group"
                  label="Pool Worker Password"
                  label-for="destinationWorkerPassword"
                >
                  <b-form-input
                    id="destinationWorkerPassword"
                    type="password"
                    v-model="poolWorkerPassword"
                    placeholder="Pool Worker Password"
                    autocomplete="new-password"
                  ></b-form-input>
                  <small class="form-text text-muted">
                    Password used by this proxy to authenticate to the pool.
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-form-group label="Group" label-for="group">
                  <b-form-select
                    v-model="groupId"
                    id="group"
                    :options="groupOptions"
                  >
                    <template #first>
                      <option :value="null">
                        Do not add these workers to a group
                      </option>
                    </template>
                  </b-form-select>
                  <small class="form-text text-muted">
                    Group to add these workers to (optional).
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
            <div class="mb-4">
              <span variant="primary" @click="summaryOpen = !summaryOpen">
                <FontAwesomeIcon
                  class="chevron"
                  icon="chevron-right"
                  :rotate="summaryOpen"
                />
                Summary
              </span>
              <b-row v-if="summaryOpen" class="mt-2">
                <b-col>
                  <div v-if="error == 'MISSING_COUNT'">Count is required.</div>
                  <div v-else-if="error == 'HIGH_COUNT'">
                    Your license only supports adding {{ maxToAdd }} more
                    worker{{ maxToAdd == 1 ? "" : "s" }}. Reduce the number of
                    workers to at most {{ maxToAdd }}.
                  </div>
                  <div v-else-if="error == 'MISSING_NAME_FORMAT'">
                    Name format is required.
                  </div>
                  <div v-else-if="error == 'MISSING_HANDLE_FORMAT'">
                    Handle format is required.
                  </div>
                  <div v-else-if="error == 'MISSING_POOL_WORKER_NAME_FORMAT'">
                    Pool worker name format is required.
                  </div>
                  <div v-else-if="error == 'INVALID_NAME_FORMAT'">
                    Invalid name format.
                  </div>
                  <div v-else-if="error == 'INVALID_HANDLE_FORMAT'">
                    Invalid handle format.
                  </div>
                  <div v-else-if="error == 'MISSING_DEVICE'">
                    Please select a device type.
                  </div>
                  <div v-else-if="error == 'MISSING_POOL'">
                    Please select a pool.
                  </div>
                  <div v-else-if="error == 'INVALID_POOL_WORKER_NAME_FORMAT'">
                    Invalid pool worker name format.
                  </div>
                  <div v-else-if="error == 'DUPLICATE_HANDLE'">
                    Handles must be unique.
                    <span v-if="namingScheme == 'numeric'">
                      Include
                      <b>%d</b>
                      in the handle to include the worker's number.
                    </span>
                    <span v-else>
                      Please ensure that each handle appears only once.
                    </span>
                  </div>
                  <div v-else-if="error == 'MISSING_WORKER_NAMES'">
                    Enter at least one worker name.
                  </div>
                  <div v-else-if="error == 'MISMATCHED_HANDLES_COUNT'">
                    Number of handles does not match number of worker names.
                  </div>
                  <div
                    v-else-if="error == 'MISMATCHED_POOL_WORKER_NAMES_COUNT'"
                  >
                    Number of pool worker names does not match number of worker
                    names.
                  </div>
                  <div v-else>
                    Adding {{ namesList.length }} worker{{
                      namesList.length == 1 ? "" : "s"
                    }}
                    with name{{ namesList.length == 1 ? "" : "s like" }}
                    <b>{{ namesList[0] }}</b>
                    , handle{{ namesList.length == 1 ? "" : "s like" }}
                    <b>{{ handlesList[0] }}</b>
                    , and pool worker name{{
                      namesList.length == 1 ? "" : "s like"
                    }}
                    <b>{{ poolWorkerNamesList[0] }}</b>
                    .
                  </div>
                </b-col>
              </b-row>
            </div>

            <b-button
              type="submit"
              variant="primary"
              :disabled="error !== null"
            >
              Add Workers
            </b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped>
.boxed-radios >>> .form-check-inline {
  padding: 0.5rem;
  height: 38px;
  border: 1px solid transparent;
  -webkit-box-shadow: 0 0 2px 0 rgba(0, 20, 40, 0.4);
  box-shadow: 0 0 2px 0 rgba(0, 20, 40, 0.4);
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
}

svg.chevron {
  user-select: none;
  transition: transform 0.2s ease-in;
  margin-right: 0.125rem;
  margin-left: 0.125rem;
}

svg.chevron[rotate] {
  transform: rotate(90deg);
}
</style>
